import { toTwelveHour, wholeDayTimes } from './timeFormat.util';
import { NO_FIXED_TIME_LABEL, WHOLE_DAY_LABEL, standDownRollsOver } from '../constants/slotLabel.constant';
import { DateTime } from 'luxon';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE SLOT ENGINE. One schedule in, an ordered list of concrete duty slots out.
 *
 *  ⚠️ THIS FILE IS SHARED BECAUSE THE COUNT MUST AGREE.
 *
 *  The intake's right rail says "this will create 6 duties" while the operator is still
 *  typing, and `publishArrangement` creates them an hour later. Two implementations of
 *  "Mon, Wed, Fri, 20:00-02:00, in Kolkata" agree on every easy case and disagree on the
 *  overnight one, which is the case that matters. So there is ONE implementation and both
 *  sides call it — the same rule `web/src/lib/policyPreview.ts` states for the requirement
 *  columns.
 *
 *  ⚠️ EVERY CONVERSION GOES THROUGH THE IANA ZONE. Nothing here adds five hours and
 *  thirty minutes to anything. India has no DST today; if that ever changed this file
 *  would follow the tz database with no edit, and that property is worth more than the
 *  arithmetic it costs.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export const IST_ZONE = 'Asia/Kolkata';

/**
 * ⚠️ ASSERTED AT MODULE LOAD, NOT ASSUMED.
 *
 * On a runtime built without the time-zone database `Asia/Kolkata` degrades silently to
 * UTC, and every duty in the system is then issued five and a half hours early with
 * nothing on screen to say so. Node's official images ship full ICU; this is what turns
 * "ships" into "proved".
 *
 * The probe asserts only that DATA EXISTS — `offset !== 0` rather than `offset === 330` —
 * so it makes no claim about what the offset is, and would not need editing if it changed.
 */
const zoneProbe = DateTime.fromISO('2026-01-01T00:00', { zone: IST_ZONE });
if (!zoneProbe.isValid || zoneProbe.offset === 0) {
  throw new Error(
    `This runtime has no time-zone data for ${IST_ZONE}. Node must be built with full ICU — ` +
      'without it every Indian duty time silently resolves to UTC.',
  );
}

/** luxon's ISO weekday. Monday is 1, Sunday is 7. The weekday chips map straight onto it. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

/**
 * WHEN a recurrence stops. Two terminators of ONE rule, never two kinds of rule —
 * "every Mon and Wed until 30 September" and "every Mon and Wed, ten times" differ only
 * in their ending. A fourth `Recurrence` kind would duplicate the weekday list and make
 * an EMPTY weekday set reachable from a valid request, which is the one input a
 * count-terminated walk cannot survive.
 */
export type RecurrenceEnd = { kind: 'ON_DATE'; endDate: string } | { kind: 'AFTER_N'; occurrences: number };

export type Recurrence =
  | { kind: 'ONE_DAY'; date: string }
  | { kind: 'DATE_RANGE'; startDate: string; ends: RecurrenceEnd }
  | { kind: 'WEEKDAYS'; startDate: string; ends: RecurrenceEnd; weekdays: readonly Weekday[] };

/**
 * `HH:mm`, zero-padded. The schema is what guarantees the padding — see `generateSlots`.
 *
 * ⚠️ `id` IS THE SHIFT'S STABLE IDENTITY AND IT IS WHAT `slotKey` IS BUILT FROM.
 *
 * A slot key of `<date>#<position>` looks obviously right and is a silent data-corruption
 * bug: `slotOverrides[]` and `commandEntries[].perSlot[]` both key on it, so inserting a
 * shift at the TOP of the list re-points every override at the wrong shift — the detached
 * night duty quietly becomes a detached morning duty, and nothing anywhere says so. The
 * id is minted once by the service (the same "present → keep, absent → mint" rule
 * `rekeyCommandRoles` uses for a command role's key) and travels on the wire thereafter.
 */
export type ShiftInput = { id: string; name?: string | null | undefined; startTime: string; endTime: string };

export type Timing = { kind: 'NO_FIXED_TIME' } | { kind: 'SHIFTS'; shifts: readonly ShiftInput[] };

export type Schedule = { recurrence: Recurrence; timing: Timing };

export type Slot = {
  /** The BUSINESS day, `YYYY-MM-DD` in IST. Survives midnight — see `generateSlots`. */
  dutyDate: string;
  /**
   * The shift's POSITION within its day, `0` when there is no fixed time.
   *
   * ⚠️ FOR DISPLAY AND ORDERING ONLY. Never key anything on it — see `slotKey`.
   */
  slotIndex: number;
  /**
   * `2026-08-15#f3a91c02`. The per-slot identity `slotOverrides` and the per-shift mapper
   * key on, built from the shift's STABLE id rather than its position.
   */
  slotKey: string;
  shiftName: string | null;
  /**
   * Whether a time was set at all. A SEPARATE FIELD rather than a null `startsAt`, so
   * nothing ever prints "00:00-00:00": rendering asks this, window arithmetic asks
   * `startsAt`/`endsAt`. Two questions, two fields, and no overloading of `null`.
   */
  hasFixedTime: boolean;
  spansMidnight: boolean;
  /** UTC instants, ISO-8601. A `Date` is minted at the model boundary, never here. */
  startsAt: string;
  endsAt: string;
  label: string;
};

/** A schedule cannot outrun this even if a stored one is corrupt. The SHAPE bound. */
export const MAX_DUTY_DAYS = 366;

/**
 * How far a COUNT-terminated walk may SEARCH. A separate constant from `MAX_DUTY_DAYS`,
 * which bounds the OUTPUT, and the separation is measured rather than tidy.
 *
 * ⚠️ SHARING ONE BOUND WOULD SILENTLY BREAK STORED DATA. A `WEEKDAYS`/`ON_DATE` order
 * from 2026-01-01 to 2027-06-30 on Mondays WALKS 546 calendar days and COLLECTS 78 — and
 * is legal today, because the bound below counts what was COLLECTED. Applying a scan bound
 * to the date-terminated arm would turn such a row into a `RangeError` that `slotsOf`'s
 * catch swallows into `slot_count: 0`.
 */
export const MAX_RECURRENCE_SPAN_DAYS = 366;

/** Turns `recurrenceOf`'s assertion into a check. A stored `9` is dropped, never carried. */
/**
 * "N days later, as a business date, THROUGH THE ZONE."
 *
 * ⚠️ NEVER `+ n * 86_400_000`. A day is a CALENDAR unit and a millisecond count is a
 * DURATION; they agree until they do not, and the case where they disagree is the one
 * nobody tests. The zone is what makes a day a day — this file's header says so, and
 * `reanchor` in `arrangement.service.ts` computes exactly this and is refactored onto it.
 *
 * (An HOUR, by contrast, genuinely is a duration — which is why the standing-order
 * generator's fill-open lead is plain millisecond arithmetic and this is not.)
 */
export const addIstDays = (date: string, days: number): string => {
  const moved = istDay(date).plus({ days }).toISODate();
  if (moved === null) throw new RangeError(`Could not move "${date}" by ${String(days)} days`);
  return moved;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5 §11b] THE FIRST DAY OF AN IST MONTH, N MONTHS BACK — `addIstDays`' MONTH TWIN.
 *
 *  This file had `addIstDays` and `istDutyDate` and no month helper at all, so §11's three-month
 *  window had nothing to compute its start from.
 *
 *  ⚠️ `plus({ months })`, NEVER `± n * 30` DAYS. `addIstDays`' own rule, one unit up and with
 *  more force: a month is a CALENDAR unit of 28 to 31 days, so an arithmetic approximation is
 *  wrong for most of the year and wrong differently each time. Luxon lands on the first of the
 *  month by `startOf('month')`, which is also what makes the result independent of today's
 *  day-of-month — the trap in "three months ago" computed from the 31st.
 *
 *  ⚠️ AND IT ANSWERS A **BUSINESS DATE** STRING, because that is what it is compared against.
 *  `assignments.duty_date` is `YYYY-MM-DD` in IST ([P3a]), so the whole comparison stays
 *  lexicographic and no instant is ever constructed. Returning a `Date` would reintroduce
 *  exactly the crossing [IST-FIX] found broken twice.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const istMonthStart = (date: string, monthsBack = 0): string => {
  const moved = istDay(date).minus({ months: monthsBack }).startOf('month').toISODate();
  if (moved === null) throw new RangeError(`Could not move "${date}" back ${String(monthsBack)} months`);
  return moved;
};

export const isWeekday = (value: number): value is Weekday => (WEEKDAYS as readonly number[]).includes(value);

/**
 * The shift id a whole-day duty uses. A word rather than a number, so it can never
 * collide with a minted shift id and so a slot key reads as what it is.
 */
export const WHOLE_DAY_SLOT_ID = 'day';

const istDay = (date: string): DateTime => {
  const day = DateTime.fromISO(date, { zone: IST_ZONE }).startOf('day');
  if (!day.isValid) throw new RangeError(`"${date}" is not a calendar date`);
  return day;
};

const toUtcIso = (value: DateTime): string => {
  const iso = value.toUTC().toISO();
  if (iso === null) throw new RangeError('A slot boundary did not convert to an instant');
  return iso;
};

const applyTime = (day: DateTime, time: string): DateTime => {
  const hour = Number(time.slice(0, 2));
  const minute = Number(time.slice(3, 5));
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour > 23 || minute > 59) {
    throw new RangeError(`"${time}" is not an HH:mm time of day`);
  }
  return day.set({ hour, minute, second: 0, millisecond: 0 });
};

/**
 * ⚠️ EXPORTED. Two callers outside `generateSlots` need the dates without the slots:
 * `arrangement.schema.ts`'s feasibility refine (through `lastDutyDate`) and the service's
 * `end_date` materialisation. One implementation of "when does this end", three readers.
 */
export const dutyDates = (recurrence: Recurrence): string[] => {
  if (recurrence.kind === 'ONE_DAY') return [istDay(recurrence.date).toISODate() ?? recurrence.date];

  const from = istDay(recurrence.startDate);
  const wanted = recurrence.kind === 'WEEKDAYS' ? new Set<number>(recurrence.weekdays) : null;
  const dates: string[] = [];

  // Calendar arithmetic, not `+ 86_400_000`: `plus({ days: 1 })` on a zoned DateTime
  // stays on the same wall-clock day across any offset change, which is the whole reason
  // a range is WALKED rather than computed from a millisecond difference.
  if (recurrence.ends.kind === 'ON_DATE') {
    const to = istDay(recurrence.ends.endDate);
    if (to < from) throw new RangeError('A schedule ends before it starts');

    for (let day = from; day <= to; day = day.plus({ days: 1 })) {
      if (wanted === null || wanted.has(day.weekday)) {
        const iso = day.toISODate();
        if (iso !== null) dates.push(iso);
      }
      // ⚠️ UNCHANGED, AND DELIBERATELY STILL COUNTING COLLECTED DATES. See
      // `MAX_RECURRENCE_SPAN_DAYS` — every recurrence stored before this pass walks
      // this identical loop and must keep returning the identical answer.
      if (dates.length > MAX_DUTY_DAYS) throw new RangeError('A schedule cannot span more than a year');
    }
    return dates;
  }

  /*
   * ⚠️ THE COUNT TERMINATES ON MATCHES, NOT ON CALENDAR DAYS. The push sits INSIDE the
   * weekday filter, so "ten times" on Mon/Wed is ten Mondays-and-Wednesdays across five
   * weeks. One line of placement is the entire semantics of the feature.
   *
   * ⚠️ AND THE HORIZON IS CHECKED BEFORE THE MATCH, BECAUSE A COUNT-TERMINATED LOOP OVER
   * A FILTER THAT MATCHES NOTHING NEVER TERMINATES. `arrangement.model.ts`'s `weekdays`
   * carries no `enum` and no minimum, so a corrupt stored `weekdays: []` reaches here.
   * MEASURED: without this check that input HANGS THE PROCESS; with it, it throws at
   * iteration 366. A hang is the one failure a test cannot report, so the bound is not
   * optional and is not a nicety.
   */
  const { occurrences } = recurrence.ends;
  const lastScanned = from.plus({ days: MAX_RECURRENCE_SPAN_DAYS - 1 });

  for (let day = from; dates.length < occurrences; day = day.plus({ days: 1 })) {
    if (day > lastScanned) {
      throw new RangeError(
        `Only ${String(dates.length)} of ${String(occurrences)} duty days fall within ` +
          `${String(MAX_RECURRENCE_SPAN_DAYS)} days of the start date`,
      );
    }
    if (wanted === null || wanted.has(day.weekday)) {
      const iso = day.toISODate();
      if (iso !== null) dates.push(iso);
    }
  }

  /*
   * No output check here, and that is not an oversight: at most one date is pushed per
   * walked day and the walk is capped, so `dates.length > MAX_DUTY_DAYS` is unreachable.
   * A guard nobody can trip is a guard nobody should trust — §11's own argument.
   */
  return dates;
};

/**
 * The last duty date a recurrence implies, or `null` when it implies none.
 *
 * ⚠️ TOTAL, AND THAT IS LOAD-BEARING RATHER THAN DEFENSIVE. It is called from a zod
 * refine, and MEASURED on zod 4.4.3: a throw inside a refine ESCAPES `safeParse` as the
 * raw error rather than becoming an issue. `error.middleware.ts` maps an unrecognised
 * error to an opaque 500, so an unguarded call here would turn a fat-fingered count into
 * "Internal server error" on a value the form's own `max` offered.
 */
export const lastDutyDate = (recurrence: Recurrence): string | null => {
  try {
    const dates = dutyDates(recurrence);
    return dates[dates.length - 1] ?? null;
  } catch {
    return null;
  }
};

/**
 * THE ENGINE.
 *
 * ⚠️ THE DUTY DATE IS THE DATE THE SHIFT STARTS, IN IST, ALWAYS.
 *
 * A 20:00-02:00 shift on 15 August is ONE duty on 15 August — not two half-duties, and
 * not a duty on the 16th. That is what makes "Mon, Wed, Fri" three duty days rather than
 * six halves, it is what the order sheet prints, and it is what a division's inbox groups
 * by. PA_REQUIREMENTS §7 calls it a business-day concept that must survive midnight.
 *
 * ⚠️ `endTime <= startTime` SPANS TO THE NEXT DAY, and the `<=` is deliberate: this
 * function is TOTAL and may never emit `startsAt === endsAt`. Equal times are separately
 * REFUSED by `arrangement.schema.ts` with a field-level message, so the `<=` here is the
 * belt to that schema's braces rather than the user-facing rule.
 *
 * ⚠️ THAT COMPARISON IS A STRING COMPARISON, AND IT IS SOUND ONLY BECAUSE THE TIMES ARE
 * ZERO-PADDED `HH:mm`. The schema's `timeOfDaySchema` is what guarantees the padding, so
 * lexicographic order is chronological order. `"7:00"` never reaches here.
 */
export const generateSlots = (schedule: Schedule): Slot[] => {
  const dates = dutyDates(schedule.recurrence);

  if (schedule.timing.kind === 'NO_FIXED_TIME') {
    return dates.map((dutyDate) => {
      const dayStart = istDay(dutyDate);
      return {
        dutyDate,
        slotIndex: 0,
        slotKey: `${dutyDate}#${WHOLE_DAY_SLOT_ID}`,
        shiftName: null,
        hasFixedTime: false,
        spansMidnight: false,
        /*
         * THE WHOLE IST DAY, never null. FR-5.2.4 says "no fixed time (whole duty day)"
         * and FR-5.3.7 already fixes the closing edge at "end of the duty date when no
         * time is set". A nullable instant would push a `?? endOfDay` branch into the
         * delete guard, the swap window, P4's reminder and P5's conflict index — four
         * places to forget, where forgetting reads as "no window" and therefore "always
         * open".
         */
        startsAt: toUtcIso(dayStart),
        endsAt: toUtcIso(dayStart.plus({ days: 1 })),
        label: `${dayStart.toFormat('ccc d LLL')} · ${NO_FIXED_TIME_LABEL}`,
      };
    });
  }

  const { shifts } = schedule.timing;
  if (shifts.length === 0) throw new RangeError('A shift schedule needs at least one shift');

  return dates.flatMap((dutyDate) => {
    const dayStart = istDay(dutyDate);

    return shifts.map((shift, slotIndex) => {
      const spansMidnight = shift.endTime <= shift.startTime;
      const start = applyTime(dayStart, shift.startTime);
      const end = applyTime(spansMidnight ? dayStart.plus({ days: 1 }) : dayStart, shift.endTime);
      const name = shift.name?.trim() ? shift.name.trim() : null;

      return {
        dutyDate,
        slotIndex,
        slotKey: `${dutyDate}#${shift.id}`,
        shiftName: name,
        hasFixedTime: true,
        spansMidnight,
        startsAt: toUtcIso(start),
        endsAt: toUtcIso(end),
        label: [
          dayStart.toFormat('ccc d LLL'),
          name,
          // [P4-UX.2] 12-hour for the reader; the STORED shift times stay `HH:mm`.
          `${toTwelveHour(shift.startTime)}–${toTwelveHour(shift.endTime)}${spansMidnight ? ' (+1 day)' : ''}`,
        ]
          .filter((part): part is string => part !== null)
          .join(' · '),
      };
    });
  });
};

/**
 * ⚠️ [C1c §1b] A WALL-CLOCK TIME ON A BUSINESS DATE → AN INSTANT. THE OTHER CROSSING.
 *
 * `istDutyDate` below answers "which IST day is this instant in"; this answers the same
 * question in the other direction, and it exists so that a reporting time is resolved by the
 * SAME `istDay`/`applyTime` pair that produced the shift's `starts_at`. §1b's instruction was
 * "do not invent a second time model", and re-deriving `20:00 IST on the 15th` anywhere else
 * would be exactly that — one of them would eventually be wrong across a DST-less border in a
 * way nothing reports.
 *
 * ⚠️ IT IS FOR A TIME ON *THE* DUTY DATE, AND NEVER ROLLS FORWARD. A shift may cross midnight
 * and `generateSlots` pushes its END onto the next day for that reason; a reporting time is
 * when the force assembles BEFORE a duty and has no such case. If one ever appears it is a new
 * function with its own name, never a boolean added here.
 *
 * Throws on a malformed date or time, exactly as `istDay` and `applyTime` do — both are
 * regex-guarded upstream, so a throw here means a stored column has been corrupted.
 */
export const istInstant = (dutyDate: string, time: string): Date =>
  new Date(toUtcIso(applyTime(istDay(dutyDate), time)));

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5b §3e] A CARD'S OWN REPORTING TIME — THE ONE RULE, READ BY BOTH SIDES OF THE WIRE.
 *
 *  A WHOLE-DAY card keeps the time the operator typed: there is no shift to say when the force
 *  assembles. A SHIFT-BEARING card has NONE of its own, because each of its duties reports at its
 *  OWN shift start (`reportingInstantOf` below) — decision 4, which REVERSES [C2b §1c]'s
 *  card-level minimum for shift cards. That minimum was one instant stamped on every duty of the
 *  card, so a card with a 05:30 and a 17:00 shift told the evening duty to report at 05:30 — nine
 *  such duties are live on `pamsDev` — and opened its marking window eleven and a half hours early.
 *
 *  ⚠️ ONE FUNCTION, CALLED BY THE BUILDER (`formValues.ts`' `reportingTimeOf`), THE STORED-CARD
 *  WRITER AND EVERY READER OF A CARD (`toDto`, the order sheet's "Report to", the duplicate and
 *  `masterArchive` paths). Two expressions of "does this card have a reporting time" drifted once
 *  already; this is why there is one.
 *
 *  ⚠️ `timingKind` TAKES THE BUILDER'S `SINGLE`/`MULTIPLE` SPELLINGS AS WELL AS THE STORED
 *  `SHIFTS`, and all three mean "shift-bearing". A union rather than `string`, so a typo in a
 *  caller is a compile error rather than a card that silently loses its time.
 *
 *  ⚠️ BLANK IS NULL. `TimeField` emits `''` when cleared and `timeOfDaySchema` refuses it.
 *
 *  ⚠️ `pwa/` MUST NEVER IMPORT THIS — this file imports luxon, about 13% of that app's budget.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const cardReportingTimeOf = (card: {
  timingKind: Timing['kind'] | 'SINGLE' | 'MULTIPLE';
  reportingTime: string | null | undefined;
}): string | null =>
  card.timingKind === 'NO_FIXED_TIME' && (card.reportingTime ?? '').trim().length > 0
    ? (card.reportingTime ?? null)
    : null;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5b §3e] A DUTY'S REPORTING INSTANT — ITS OWN START WHEN IT HAS ONE.
 *
 *  `hasFixedTime ? startsAt : reportingAt`. A fixed-time duty reports at its shift start whatever
 *  its stored `reporting_at` says, so a legacy row stamped with the card-level minimum reads right
 *  on every surface with NO migration: the derivation is on READ, and publish stamps the same
 *  value, so a new row and a healed row are indistinguishable.
 *
 *  ⚠️ A WHOLE-DAY DUTY KEEPS ITS STORED INSTANT, because there `startsAt` is 00:00 IST — the
 *  window's bound, never a time anybody reports at.
 *
 *  ⚠️ IT IS GENERIC OVER THE INSTANT'S TYPE, so the API hands it `Date`s and a test hands it ISO
 *  strings, and neither pays a conversion. `api/src/utils/dutyWindow.util.ts`' `dutyReportingAt`
 *  is its one server caller, and every DTO that carries `reportingAt` goes through that.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const reportingInstantOf = <T extends Date | string>(duty: {
  hasFixedTime: boolean;
  startsAt: T;
  reportingAt: T | null;
}): T | null => (duty.hasFixedTime ? duty.startsAt : duty.reportingAt);

/**
 * [STAND-DOWN] A WHOLE-DAY DUTY'S STAND-DOWN TIME → AN INSTANT — the "new function with its own
 * name" `istInstant`'s header asks for, because this one DOES roll forward.
 *
 * A stand-down at or before the reporting time is the next morning (`standDownRollsOver`), which
 * is `generateSlots`' own rule for a shift's end. Resolved through `istInstant`, so there is still
 * one crossing from a wall-clock time to an instant.
 */
export const istStandDownInstant = (dutyDate: string, reportingTime: string | null, standDownTime: string): Date =>
  istInstant(standDownRollsOver(reportingTime, standDownTime) ? addIstDays(dutyDate, 1) : dutyDate, standDownTime);

/**
 * ⚠️ [C1c §1a] AN INSTANT → THE `HH:mm` A READER IN IST WOULD SEE. `istInstant`'s inverse.
 *
 * IST EXPLICITLY, because a duty stores instants and every screen and sheet that renders one
 * is showing an officer the hour they were ORDERED for — an instant formatted in the caller's
 * zone prints the wrong number everywhere but here. `slotTimeLabel` below is its first caller
 * and used to spell this inline; sharing it is what keeps a printed sheet's reporting time and
 * its duty window in one format.
 */
export const istTimeOfDay = (instant: Date | string): string =>
  (typeof instant === 'string' ? DateTime.fromISO(instant, { zone: IST_ZONE }) : DateTime.fromJSDate(instant))
    .setZone(IST_ZONE)
    .toFormat('HH:mm');

/**
 * Which IST duty date an instant falls in.
 *
 * ⚠️ NOT A ROUND TRIP FOR `duty_date`, WHICH IS STORED AS A `YYYY-MM-DD` STRING.
 *
 * A business date and an instant are different kinds of thing, and storing a business
 * date as an instant is how one gets written in one zone and read in another. `pa_date`
 * and `duty_date` are therefore both strings — lexicographic order is chronological order,
 * and a range filter is a plain string range. All actual time arithmetic uses `starts_at`
 * and `ends_at`, which are instants because that is what they are.
 *
 * This exists for the one question that genuinely crosses the two: "what is today's duty
 * date, here?" — which a `now` cannot answer without a zone.
 */
/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [D4.5 §3d] THE TIME FRAGMENT OF A SLOT LABEL, WITHOUT THE DATE.
 *
 *  `generateSlots` builds `label` as `date · name · times`, which is right where the date is
 *  the only thing identifying the row. §3d gives the inbox its own Date column, so the Time
 *  column would otherwise repeat it — D4.2's "a heading over a labelled field says the same
 *  thing twice", one level down.
 *
 *  ⚠️ IT TAKES THE INSTANTS, BECAUSE THAT IS WHAT A DUTY STORES. `deployments` carries
 *  `starts_at`/`ends_at` plus `shift_name`, `has_fixed_time` and `spans_midnight`; the
 *  `HH:mm` strings live on the ARRANGEMENT's schedule and are not on the row this renders.
 *  Parsing the stored `slot_label` back apart was the other option and is worse: it makes a
 *  display string load-bearing and breaks the day anybody re-words it.
 *
 *  ⚠️ IST EXPLICITLY, ON BOTH ENDS. An instant formatted in the caller's zone prints the
 *  wrong hour everywhere but here, and this renders on a screen whose whole subject is when
 *  a constable reports. `istDutyDate` below is the same crossing asked the other way round.
 *
 *  ⚠️ IT IS A SECOND EXPRESSION OF `generateSlots`' TIME FRAGMENT, AND THAT IS A DELIBERATE
 *  TRADE. Refactoring `generateSlots` onto it would rewrite the producer of a STORED column
 *  (`deployments.slot_label`) for the sake of a display string. What keeps the two honest is
 *  an assertion in `slot.util.test.ts` that this renders exactly the fragment `generateSlots`
 *  puts into `label` for the same shift — not care.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const slotTimeLabel = (duty: {
  hasFixedTime: boolean;
  shiftName?: string | null;
  startsAt: Date | string;
  endsAt: Date | string;
  spansMidnight?: boolean;
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3a §6] REQUIRED AND NULLABLE, NEVER OPTIONAL — AND THE DIFFERENCE IS THE POINT.
   *
   *  Optional puts `undefined` in the omitted position, so a caller that forgets it goes on
   *  saying `Whole day` with nothing to say so. Required breaks the build at all three call
   *  sites, which is exactly what a new fact on an existing string should do (divergence #100's
   *  rule for `orgUnitSummarySchema`, applied to a signature).
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  reportingAt: Date | string | null;
  /** [STAND-DOWN] Required and nullable, for `reportingAt`'s reason: a caller that forgets it breaks the build. */
  standDownAt: Date | string | null;
}): string => {
  /*
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3a §6] THE GATE IS `!hasFixedTime`, AND A VALUE-GATE HERE WOULD BE CATASTROPHIC.
   *
   *  `if (duty.reportingAt) return From …` reads like the same rule and is not. A fixed-time
   *  duty's reporting instant is its OWN start ([amended C5b §3e]: `reportingInstantOf`), so it is
   *  never null there — it was the card-level minimum of the shift starts before that pass, and
   *  never null then either. A value-gate would therefore replace `6:00 AM–2:00 PM` with
   *  `From 6:00 AM` on essentially every ordinary duty in the system.
   *
   *  §6 substitutes ONLY where there is no shift to state, which is `generateSlots`'
   *  `NO_FIXED_TIME` branch and nothing else. `slot.util.test.ts` carries the negative control.
   * ─────────────────────────────────────────────────────────────────────────────────────
   */
  if (!duty.hasFixedTime) {
    /* [STAND-DOWN] `From 3:00 PM`, `3:00 PM–9:00 PM` or `Until 9:00 PM` — still gated on `hasFixedTime`. */
    return (
      wholeDayTimes(
        duty.reportingAt === null ? null : istTimeOfDay(duty.reportingAt),
        duty.standDownAt === null ? null : istTimeOfDay(duty.standDownAt),
      ) ?? WHOLE_DAY_LABEL
    );
  }

  const name = duty.shiftName?.trim() ? duty.shiftName.trim() : null;
  const times = `${toTwelveHour(istTimeOfDay(duty.startsAt))}–${toTwelveHour(istTimeOfDay(duty.endsAt))}${
    duty.spansMidnight ? ' (+1 day)' : ''
  }`;
  return name ? `${name} · ${times}` : times;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [LOCATION-INTEL.2] A DUTY'S DATE AND TIME AS A SLOT LABEL WRITES THEM — AND NOTHING FROM ITS ORDER.
 *
 *  A stored `slot_label` is `date · shift name · times`, and the shift name is the order's free text: it can
 *  name a place, a VIP or an event. A reader that may state WHEN an officer is on duty and nothing more — the
 *  Locational Intel card on an order outside the viewer's area, FR-3.6's "on duty, 20:00–02:00, and nothing
 *  more" — builds the label here, from the duty's own date and instants, with no name to pass.
 *
 *  ⚠️ IT FAILS CLOSED, WHICH IS WHY IT IS NOT A STRIP OF THE STORED LABEL. Removing ` · <shift_name>` from
 *  `slot_label` would leak the name the day the two columns disagreed; this never reads either.
 *
 *  ⚠️ THE DATE IS `generateSlots`' OWN EXPRESSION (`istDay`, `ccc d LLL`) and the time is `slotTimeLabel`'s, so
 *  for an UNNAMED shift this is exactly the stored label — `slot.util.test.ts` holds that, and that a NAMED
 *  shift loses its name and nothing else.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const slotDateTimeLabel = (duty: {
  dutyDate: string;
  hasFixedTime: boolean;
  startsAt: Date | string;
  endsAt: Date | string;
  spansMidnight?: boolean;
  /** Required and nullable, for `slotTimeLabel`'s reason. */
  reportingAt: Date | string | null;
  standDownAt: Date | string | null;
}): string => `${istDay(duty.dutyDate).toFormat('ccc d LLL')} · ${slotTimeLabel({ ...duty, shiftName: null })}`;

export const istDutyDate = (instant: Date | string): string => {
  const at = instant instanceof Date ? DateTime.fromJSDate(instant) : DateTime.fromISO(instant);
  const date = at.setZone(IST_ZONE).toISODate();
  if (date === null) throw new RangeError('Not an instant');
  return date;
};

/** What the right rail's "this will create" line reads. One source, both sides. */
/**
 * ⚠️ [P4] WHEN THE SWAP WINDOW CLOSES ON A DUTY THAT CARRIES NO FIXED TIME.
 *
 * FR-5.3.7: a swap is available until the end of the shift, "or end of the duty date when no
 * time is set". The first is an INSTANT comparison against `ends_at` and needs nothing from
 * here; the second is the one crossing between the two representations, and it belongs
 * beside `istDutyDate` because it is the same question asked the other way round.
 *
 * ⚠️ THE END OF THE DAY IN ASIA/KOLKATA, NOT IN UTC. A whole-day duty on the 15th stays
 * swappable until 23:59:59.999 IST on the 15th — which is 18:29:59.999Z, four and a half
 * hours BEFORE the UTC day ends. Computing it in UTC would leave a night's worth of duties
 * editable after they were over, and in the other direction would close the window while
 * the operator was still at their desk.
 */
export const endOfIstDutyDate = (dutyDate: string): string => {
  const day = DateTime.fromISO(dutyDate, { zone: IST_ZONE });
  if (!day.isValid) throw new RangeError('Not a duty date');
  const iso = day.endOf('day').toUTC().toISO();
  if (iso === null) throw new RangeError('Not a duty date');
  return iso;
};

export const summariseSlots = (
  slots: readonly Slot[],
): { slotCount: number; dutyDayCount: number; hasOvernight: boolean } => ({
  slotCount: slots.length,
  dutyDayCount: new Set(slots.map((slot) => slot.dutyDate)).size,
  hasOvernight: slots.some((slot) => slot.spansMidnight),
});

/**
 * What happens to data a control was holding when that control disappears.
 *
 * ⚠️ SILENTLY KEEPING IT AND SILENTLY DISCARDING IT ARE BOTH WRONG, IN DIFFERENT WAYS.
 * Keeping a per-shift override for a shift that no longer exists means publishing an
 * arrangement shaped by a control the operator can no longer see. Discarding the operator's
 * own typing the moment a predicate flickers means losing work to a stray keystroke.
 *
 * So the rule is drawn at what the data REFERS to rather than at what is visible:
 *
 *   • a slot override or a per-shift row addressing a slot key that no longer exists is
 *     DROPPED, because the thing it describes is gone and it can never apply again;
 *   • everything else — the shifts themselves, the second location, the command roster —
 *     is KEPT, because it is still a true statement about the arrangement and the control
 *     that shows it will come back the moment the condition does.
 *
 * ⚠️ THE KEY IS A PARAMETER, BECAUSE THE TWO SIDES OF THE WIRE SPELL IT DIFFERENTLY AND
 * NEITHER SHOULD HAVE TO KNOW THE OTHER'S SPELLING. The wire holds `slotKey`; the stored
 * sub-document holds `slot_key` (§3 — Mongo is snake_case and the model is the boundary).
 * A snake_case wrapper HERE would put a Mongo field name inside `packages/shared`, which is
 * the one thing this package must not know, so the accessor is supplied at the call site —
 * by the API, inside the file where that boundary already lives.
 *
 * ⚠️ [P3b.1] IT IS SHARED BECAUSE BOTH SIDES PRUNE. The screen doing it alone was never
 * enough: a PATCH carrying only `schedule` never passes through the screen's pruner at all,
 * and an older tab, a scripted client or a duplicate can each move the keys without ever
 * touching the rows.
 */
export const pruneToLiveSlots = <T>(
  rows: readonly T[],
  liveSlotKeys: readonly string[],
  keyOf: (row: T) => string,
): T[] => {
  const live = new Set(liveSlotKeys);
  return rows.filter((row) => live.has(keyOf(row)));
};

/** The WIRE shape of the rule above, which is what every browser-side caller holds. */
export const pruneHiddenState = <T extends { slotKey: string }>(
  rows: readonly T[],
  liveSlotKeys: readonly string[],
): T[] => pruneToLiveSlots(rows, liveSlotKeys, (row) => row.slotKey);
